import React, { useState, useCallback, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import { ProjectHierarchy } from './ProjectHierarchy';
import { ProjectListContent } from './ProjectListContent';
import { useSyncedProjects } from '../../hooks/useSyncedProjects';
import { GetProjectResponse } from '../../api/protosCompiled/project/project_pb';
import { useFetchProjectHierarchy } from '../../hooks/useFetchProjects';

// Custom hook for persistent filter state
const usePersistentFilters = () => {
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem('projectList_searchTerm') || '';
  });

  const [selectedUserId, setSelectedUserId] = useState<string | null>(() => {
    return localStorage.getItem('projectList_selectedUserId') || null;
  });

  const [showContractsOnly, setShowContractsOnly] = useState(() => {
    return localStorage.getItem('projectList_showContractsOnly') === 'true';
  });

  const [showHierarchyAssignedOnly, setShowHierarchyAssignedOnly] = useState(() => {
    return localStorage.getItem('projectList_showHierarchyAssignedOnly') === 'true';
  });

  // Save to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('projectList_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedUserId) {
      localStorage.setItem('projectList_selectedUserId', selectedUserId);
    } else {
      localStorage.removeItem('projectList_selectedUserId');
    }
  }, [selectedUserId]);

  useEffect(() => {
    localStorage.setItem('projectList_showContractsOnly', showContractsOnly.toString());
  }, [showContractsOnly]);

  useEffect(() => {
    localStorage.setItem(
      'projectList_showHierarchyAssignedOnly',
      showHierarchyAssignedOnly.toString()
    );
  }, [showHierarchyAssignedOnly]);

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedUserId(null);
    setShowContractsOnly(false);
    setShowHierarchyAssignedOnly(false);
  }, []);

  return {
    searchTerm,
    setSearchTerm,
    selectedUserId,
    setSelectedUserId,
    showContractsOnly,
    setShowContractsOnly,
    showHierarchyAssignedOnly,
    setShowHierarchyAssignedOnly,
    resetFilters,
  };
};

interface ProjectListWithHierarchyProps {
  // Props for the project list functionality
  onCreateProject: () => void;
  onEditProject: (project: GetProjectResponse.AsObject) => void;
  onLoadProject: (project: GetProjectResponse.AsObject) => void;
  isCreatingProject: boolean;
  isUpdatingProject: boolean;
  selectedProjectId: string;
  onCloseDialog: () => void;
  userId: string;
}

export const ProjectListWithHierarchy: React.FC<ProjectListWithHierarchyProps> = ({
  onCreateProject,
  onEditProject,
  onLoadProject,
  isCreatingProject,
  isUpdatingProject,
  selectedProjectId,
  onCloseDialog,
  userId,
}) => {
  const theme = useTheme();
  const { projects: projectList, error, isLoading } = useSyncedProjects();
  const { data: hierarchyList } = useFetchProjectHierarchy(userId);

  // Use persistent filters
  const {
    searchTerm,
    setSearchTerm,
    selectedUserId,
    setSelectedUserId,
    showContractsOnly,
    setShowContractsOnly,
    showHierarchyAssignedOnly,
    setShowHierarchyAssignedOnly,
    resetFilters,
  } = usePersistentFilters();

  // State for hierarchy selection (not persistent)
  const [selectedHierarchyId, setSelectedHierarchyId] = useState<string | null>(null);
  const [createProjectHierarchyId, setCreateProjectHierarchyId] = useState<string | null>(null);

  // Handle hierarchy selection
  const handleHierarchySelect = useCallback((hierarchyId: string | null) => {
    setSelectedHierarchyId(hierarchyId);
  }, []);

  // Handle project click from hierarchy
  const handleProjectClick = useCallback(
    (project: GetProjectResponse.AsObject) => {
      onLoadProject(project);
    },
    [onLoadProject]
  );

  // Handle create project with hierarchy ID
  const handleCreateProjectWithHierarchy = useCallback(
    (hierarchyId: string) => {
      setCreateProjectHierarchyId(hierarchyId);
      onCreateProject();
    },
    [onCreateProject]
  );

  // Get projects from selected hierarchy or all projects
  const getProjectsForDisplay = useCallback(() => {
    if (selectedHierarchyId && hierarchyList) {
      // Find the selected hierarchy node and get its projects
      const hierarchyNode = hierarchyList.find(h => h.id === selectedHierarchyId);
      if (hierarchyNode?.projectsList) {
        // Convert Project[] to GetProjectResponse.AsObject[]
        return hierarchyNode.projectsList.map(project => ({
          project: project,
        }));
      }
    }
    return projectList || [];
  }, [selectedHierarchyId, hierarchyList, projectList]);

  // Filter projects based on search and user filter
  const filteredProjects = getProjectsForDisplay().filter(project => {
    if (!project.project) return false;

    const matchesSearch =
      searchTerm === '' ||
      project.project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.project.address.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesUser =
      selectedUserId === null || project.project.createdbyuserid === selectedUserId;

    return matchesSearch && matchesUser;
  });

  return (
    <Box
      sx={{
        height: 'calc(100vh - 100px)', // Subtract header height (typically 64px)
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        px: 1, // Small horizontal padding
        py: 0, // No vertical padding to move content closer to header
      }}
    >
      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          bgcolor: theme.palette.background.paper,
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: theme.shadows[1],
          m: 0, // Remove any default margins
        }}
      >
        {/* Project Hierarchy Panel */}
        <ProjectHierarchy
          selectedHierarchyId={selectedHierarchyId}
          onHierarchySelect={handleHierarchySelect}
          userId={userId}
          projects={projectList}
          onProjectClick={handleProjectClick}
          onProjectEdit={onEditProject}
          onCreateProject={handleCreateProjectWithHierarchy}
        />

        {/* Project List Panel */}
        <Box
          sx={{
            flex: '1 1 0',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ProjectListContent
            projects={filteredProjects || []}
            error={error as Error | null}
            isLoading={isLoading}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onEditProject={onEditProject}
            onLoadProject={onLoadProject}
            isCreatingProject={isCreatingProject}
            isUpdatingProject={isUpdatingProject}
            selectedProjectId={selectedProjectId}
            onCloseDialog={onCloseDialog}
            selectedHierarchyId={selectedHierarchyId}
            selectedUserId={selectedUserId}
            onUserFilterChange={setSelectedUserId}
            showContractsOnly={showContractsOnly}
            onContractFilterChange={setShowContractsOnly}
            showHierarchyAssignedOnly={showHierarchyAssignedOnly}
            onHierarchyFilterChange={setShowHierarchyAssignedOnly}
            onResetFilters={resetFilters}
            createProjectHierarchyId={createProjectHierarchyId}
          />
        </Box>
      </Box>
    </Box>
  );
};
